import {
  Select,
  Space,
  Stack,
  Tabs,
  Text,
  type ComboboxItem,
  type SelectProps,
} from '@mantine/core';
import {
  createFileRoute,
  Outlet,
  type RouteMatch,
  useNavigate,
  useChildMatches,
} from '@tanstack/react-router';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  reportWithIdSchema,
  type ReportWithId,
} from '#features/scout/Schema/Reports.schema';
import {
  formatAbn,
} from '@specia-accountant/shared';

interface ClientSelectData extends ComboboxItem {
  companyName: string;
  ABN: string;
  ssid: string;
}

type ReportType = 'activityStatement' | 'generalInterests' | 'ica' | 'ita';

export const Route = createFileRoute(
  '/_app/_authenticated/mydigitalworkers/scout/_scoutLayout/reports/$reportId',
)({
  component: RouteComponent,
  beforeLoad() {
    return {
      crumb: 'Reports',
    };
  },
  loader: async (opts) => {
    const report = await opts.context.queryClient.ensureQueryData({
      queryKey: [
        'reporter',
        'reports',
        opts.params.reportId,
      ],
    });
    const parsedReport = reportWithIdSchema.safeParse(report);
    if (!parsedReport.success) {
      console.warn('Report schema validation failed', {
        report,
        parsedReport,
      });
      // return report;
    }
    return parsedReport.data;
  },
});

const renderClientSelectOption: SelectProps['renderOption'] = (item) => {
  const {
    option,
  } = item;
  const client = option as ClientSelectData;
  if (client.value) {
    try {
      return (
        <Stack
          gap="sm"
        >
          <div>
            <Text
              size="sm"
            >
              { client.label }
            </Text>
            <Text
              size="xs"
            >
              { formatAbn(client.value) }
            </Text>
          </div>
        </Stack>
      );
    } catch (err) {
      console.warn(err);
    }
  }
};

function RouteComponent() {
  const childPathMatch = useChildMatches({
    select: matches => matches.at(0)?.id.split('/').at(-1) ?? 'activityStatement',
  });
  const reportData: ReportWithId = Route.useLoaderData();
  const navigate = useNavigate();
  const {
    reportId,
  } = Route.useParams();
  const {
    clients,
  } = reportData;
  const firstClient = clients?.length ? clients[0] : null;
  const clientsSelectOptions = useMemo(() => {
    if (clients) {
      return clients.map((client) => {
        const {
          ABN,
          companyName,
          ...remainingProps
        } = client;
        return {
          value: String(ABN),
          label: companyName,
          ...remainingProps,
        };
      });
    }
  }, [clients]);
  const [
    abn,
    setAbn,
  ] = useState<string>(clients![0].ABN);
  const [
    reportType,
    setReportType,
  ] = useState<ReportType>(childPathMatch as ReportType);
  const navigateParams = useMemo(() => {
    return {
      from: Route.fullPath,
      params: {
        reportId,
        abn,
      },
    };
  }, [
    abn,
    reportId,
    Route.fullPath,
  ]);
  useEffect(() => {
    switch (reportType) {
      case 'activityStatement':
        navigate({
          to: '/mydigitalworkers/scout/reports/$reportId/$abn/activityStatement',
          ...navigateParams,
        });
        break;
      case 'generalInterests':
        navigate({
          to: '/mydigitalworkers/scout/reports/$reportId/$abn/generalInterests',
          ...navigateParams,
        });
        break;
      case 'ica':
        navigate({
          to: '/mydigitalworkers/scout/reports/$reportId/$abn/ica',
          ...navigateParams,
        });
        break;
      case 'ita':
        navigate({
          to: '/mydigitalworkers/scout/reports/$reportId/$abn/ita',
          ...navigateParams,
        });
        break;
      default:
        navigate({
          to: '/mydigitalworkers/scout/reports/$reportId/$abn/activityStatement',
          ...navigateParams,
        });
        break;
    }
  }, [
    navigateParams,
    reportType,
  ]);
  return (
    <>
      <Select
        placeholder={ firstClient?.ABN }
        defaultValue={ firstClient?.ABN }
        renderOption={ renderClientSelectOption }
        data={ clientsSelectOptions }
        value={ abn }
        label="Select Client"
        onChange={ (evt) => {
          console.debug(`ABN change`, evt);
          if (typeof evt === 'string') {
            setAbn(evt);
          }
        } }
      />
      <Tabs
        variant="pills"
        value={ reportType }
        onChange={ (evt) => {
          console.debug(`Report type change`, evt);
          if (typeof evt === 'string') {
            setReportType(evt as ReportType);
          }
        } }
      >
        <Tabs.List>
          <Tabs.Tab
            value="activityStatement"
            leftSection=""
          >
            Activity Statement
          </Tabs.Tab>
          <Tabs.Tab
            value="generalInterests"
            leftSection=""
          >
            General Interests
          </Tabs.Tab>
          <Tabs.Tab
            value="ica"
            leftSection=""
          >
            Integrated Client Account
          </Tabs.Tab>
          <Tabs.Tab
            value="ita"
            leftSection=""
          >
            Income Tax Account
          </Tabs.Tab>
        </Tabs.List>
        <Space
          h="md"
        />
        <Outlet />
      </Tabs>
    </>
  );
}
